import type { DefaultOptions, QueryClientConfig } from '@tanstack/react-query'
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import type { PropsWithChildren } from 'react'
import { useState } from 'react'

import { isHttpClientErrorResponse } from 'aem-libs-api/src/utility/HttpClient'
import { useAppContext } from 'aem-libs-react-ts/src/hooks/useAppContext'


const defaultQueryOptions: DefaultOptions['queries'] = {
  refetchInterval: false,
  refetchIntervalInBackground: false,
  refetchOnWindowFocus: false,
  retry: false,
  refetchOnMount: false,
  refetchOnReconnect: false
}

interface QueryProviderProps {
  clientOptions?: QueryClientConfig
  ignoredApiErrorPaths?: string[]
  throwOnAnyErrorStatus?: boolean
}

export const QueryProvider = ({
  children,
  clientOptions,
  ignoredApiErrorPaths,
  throwOnAnyErrorStatus
}: PropsWithChildren<QueryProviderProps>) => {
  const { setError, loggingOptions } = useAppContext()
  const [client] = useState<QueryClient>(() => {
    const options = clientOptions ?? {
      queryCache: new QueryCache({
        onError: (error: unknown, query) => {
          if (!isHttpClientErrorResponse(error)) {
            console.error('Unknown error:', error?.toString())
            loggingOptions?.client?.error({
              type: 'API',
              data: {
                response: { data: { error: error?.toString(), queryKeys: query.queryKey } }
              }
            })
            return
          }

          const status = error?.response?.status
          const url = error?.response?.url

          loggingOptions?.client?.error({
            type: 'API',
            data: {
              request: { method: error.request.method, url, payload: error.request?.payload },
              response: { status, data: error.data }
            }
          })

          const ignoredUrls = ignoredApiErrorPaths ?? []

          const throwError = (status > 500 && !throwOnAnyErrorStatus) || (throwOnAnyErrorStatus && status >= 400)

          if (throwError && ignoredUrls.every((urlPart) => !url?.includes(urlPart))) {
            setError(true)
          }
        }
      }),
      defaultOptions: {
        queries: {
          ...defaultQueryOptions
        }
      }
    }

    return new QueryClient(options)
  })

  return <QueryClientProvider client={client}>{children}</QueryClientProvider>
}
